import React from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import tracking from 'dibs-tracking';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';

// components
import { SbSharedFollowSearchProvider } from '../SbSharedFollowSearchProvider/SbSharedFollowSearchProvider';
import { SbRespFollowSearchTopButton } from '../SbRespFollowSearchTopButton/SbRespFollowSearchTopButton';
import { SbRespFollowSearchBanner } from '../SbRespFollowSearchBanner/SbRespFollowSearchBanner';
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle';
import { SbRespRefineMenu } from '../SbRespRefineMenu/SbRespRefineMenu';
import { SbSharedPagination } from '../SbSharedPagination/SbSharedPagination';
import { LoadingComponent } from '../../components/global/LoadingComponent';
import { SbRespContentModuleSelector } from '../SbRespContentModuleSelector/SbRespContentModuleSelector';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs';
import { SbRespSearchResultContainer } from '../SbRespSearchResultContainer/SbRespSearchResultContainer';
import { SbRespBrowseHeader } from '../SbRespBrowseHeader/SbRespBrowseHeader';
import { SbRespSortItems } from '../SbRespSortItems/SbRespSortItems';
import { SbRespPageSize } from '../SbRespPageSize/SbRespPageSize';
import { SbSharedTopTileController } from '../SbSharedTopTileController/SbSharedTopTileController';
import { SbRespCategoryTiles } from '../SbRespCategoryTiles/SbRespCategoryTiles';
import { SbSharedBillboardAd } from '../SbSharedBillboardAd/SbSharedBillboardAd';
import { SbSharedSkyscraperAd } from '../SbSharedSkyscraperAd/SbSharedSkyscraperAd';
import { SbRespNullSearchResults } from '../SbRespNullSearchResults/SbRespNullSearchResults';
import { SbSharedSearchSuggestions } from '../SbSharedSearchSuggestions/SbSharedSearchSuggestions';
import { SbSharedSearchRedirectNotice } from '../SbSharedSearchRedirectNotice/SbSharedSearchRedirectNotice';
import { SbSharedRelatedItems } from '../SbSharedRelatedItems/SbSharedRelatedItems';
import { SbRespMoreWaysToBrowse } from '../SbRespMoreWaysToBrowse/SbRespMoreWaysToBrowse';
import { SbRespNotificationUnavailableItem } from '../SbRespNotifications/SbRespNotificationUnavailableItem/SbRespNotificationUnavailableItem';
import { SbRespPriceTiles } from '../SbRespPriceTiles/SbRespPriceTiles';
import { SbRespStickyMenu } from '../SbRespStickyMenu/SbRespStickyMenu';
import { SharedRecentlyViewedItems } from '../../shared/SharedRecentlyViewedItems/SharedRecentlyViewedItems';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';
import { TradePromoLoader } from '../../global/TradePromo/TradePromoLoader';
import { SbRespTradeOnApprovalLoadingBanner } from '../SbRespTradeOnApprovalPromo/SbRespTradeOnApprovalPromoBanner/SbRespTradeOnApprovalLoadingBanner';
import { SbSharedPersonalizedFilterToast } from '../SbSharedPersonalizedFilterToast/SbSharedPersonalizedFilterToast';

// constants
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
import { FILTER_REFINE_SEARCH } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants';
import { MIN_THIN_PAGE_COUNT } from '../../constants/sbConstants';
import { VERTICALS } from '../../constants/uriConstants';
const { SEARCH } = pageTypes;

// styles
import styles from './SbRespComponents.scss';

const SbRespTradeOnApprovalPromoBannerLazy = React.lazy(() =>
    import(/* webpackChunkName: "TradeOnApprovalPromo" */ '../SbRespTradeOnApprovalPromo/SbRespTradeOnApprovalPromoBanner/SbRespTradeOnApprovalPromoBannerLazy')
);

function getSearchTerm(filters, isSearchPage) {
    const searchKey = isSearchPage ? SEARCH : FILTER_REFINE_SEARCH;

    return get(filters.filter(({ name }) => name === searchKey), '[0].values[0].urlLabel');
}

function SbRespComponentsComponent(props) {
    const {
        viewer,
        currency,
        searchBrowse,
        user,
        showMeasurements,
        page,
        generatedUriRef,
        refetchInFlight,
        isClient,
        relay,
        relayVariables,
        shippingFilters,
    } = props;

    const {
        displayUriRef,
        filters,
        topCategoryL1,
        displayMaxNumberOfPages,
        totalResults,
        appliedFilters,
        pageType,
        canShowNoAttributeSearch,
    } = searchBrowse;

    const {
        isContentModuleEnabled,
        isBillboardAdEnabled,
        isMoreWaysToBrowseEnabled,
        isSkyscraperAdEnabled,
        isThinSearchBrowseEnabled,
        isFollowSearchEnabled,
    } = viewer;
    const { canShowInternalSellerName, canShowSellerName } = user || {};

    const isSearch = pageType === SEARCH;
    const isFurniture = topCategoryL1 === VERTICALS.FURNITURE;
    const canShowCreatorName = !canShowSellerName && isFurniture;
    const showNullSearchResultPage = !totalResults;
    const isThinPage = totalResults < MIN_THIN_PAGE_COUNT && isThinSearchBrowseEnabled;
    const isThinBrowsePage = !isSearch && isThinPage;
    const searchTerm = getSearchTerm(appliedFilters, isSearch);

    const sharedProps = {
        currency,
        searchBrowse,
        viewer,
        user,
        isClient,
    };

    React.useEffect(() => {
        if (canShowNoAttributeSearch) {
            const trackingData = getAbTestTrackingData(document.cookie, 'SB_NO_ATTRIBUTE_SEARCH');
            tracking.trackAbTestEvent(trackingData, 'SEARCH_BROWSE_DESKTOP');
        }
    }, [canShowNoAttributeSearch]);

    const hasSpellCheckRedirect = get(searchBrowse, 'spellCheck.redirect.redirectFrom');
    if (showNullSearchResultPage) {
        return (
            <SbRespNullSearchResults
                user={user}
                page={page}
                viewer={viewer}
                searchTerm={searchTerm}
                uriRef={generatedUriRef}
                searchBrowse={searchBrowse}
            />
        );
    }

    return (
        <SbSharedFollowSearchProvider
            user={user}
            searchBrowse={searchBrowse}
            uriRef={generatedUriRef}
            page={page}
            searchTerm={searchTerm}
            thinTrackingEnabled={isThinPage}
        >
            {({ showFollowButton, isFollowing, followSearchValue, refetchFollow }) => (
                <React.Fragment>
                    {/* Breadcrumbs */}
                    {!isSearch && <SbSharedPageBreadcrumbs searchBrowse={searchBrowse} />}

                    {hasSpellCheckRedirect && (
                        <div className={`${styles.searchSuggestions} ${styles.collated}`}>
                            <SbSharedSearchRedirectNotice
                                className={styles.redirectNotice}
                                searchBrowse={searchBrowse}
                            />
                            <SbSharedSearchSuggestions searchBrowse={searchBrowse} />
                        </div>
                    )}

                    {/* Page Header */}
                    <div className={styles.headerContainer}>
                        <SbSharedTitle searchBrowse={searchBrowse} searchTerm={searchTerm} />
                        {isFollowSearchEnabled && (
                            <SbRespFollowSearchTopButton
                                user={user}
                                uriRef={generatedUriRef}
                                showFollowButton={showFollowButton}
                                followSearchValue={followSearchValue}
                                isFollowing={isFollowing}
                                refetchFollow={refetchFollow}
                            />
                        )}
                        {relayVariables.isTrade && isClient && (
                            <div className={styles.tradePromoMessage}>
                                <TradePromoLoader showIcon user={user} viewer={viewer} />
                            </div>
                        )}
                    </div>

                    {!hasSpellCheckRedirect && (
                        <SbSharedSearchSuggestions
                            className={`${styles.searchSuggestions} ${styles.regular}`}
                            searchBrowse={searchBrowse}
                        />
                    )}

                    {/* Broad Search Category Tiles, or, for trade users, Price Range Tiles or On-Approval banner)*/}
                    <SbSharedTopTileController
                        searchBrowse={searchBrowse}
                        categoryTiles={<SbRespCategoryTiles searchBrowse={searchBrowse} />}
                        tradeOnApprovalBanner={
                            /* FINDING-13641 Workaround until React Suspense supports server-side
                             * rendering > https://reactjs.org/docs/code-splitting.html#reactlazy
                             */
                            isClient ? (
                                <React.Suspense fallback={<SbRespTradeOnApprovalLoadingBanner />}>
                                    <SbRespTradeOnApprovalPromoBannerLazy
                                        searchBrowse={searchBrowse}
                                    />
                                </React.Suspense>
                            ) : (
                                <SbRespTradeOnApprovalLoadingBanner />
                            )
                        }
                        priceTiles={
                            <SbRespPriceTiles searchBrowse={searchBrowse} viewer={viewer} />
                        }
                        viewer={viewer}
                    />

                    <div className={styles.mainContentContainer}>
                        <ServerVarsContextConsumer>
                            {({ isTablet }) =>
                                !isTablet ? (
                                    <SbRespStickyMenu>
                                        {/* Refine Menu */}
                                        <SbRespRefineMenu
                                            {...sharedProps}
                                            filters={filters}
                                            uriRef={generatedUriRef}
                                            isTrade={relayVariables.isTrade}
                                        />

                                        {/* Ad */}
                                        {!isThinPage && isSkyscraperAdEnabled && (
                                            <SbSharedSkyscraperAd searchBrowse={searchBrowse} />
                                        )}
                                    </SbRespStickyMenu>
                                ) : (
                                    <div className={styles.refineMenuContainer}>
                                        {/* Refine Menu */}
                                        <SbRespRefineMenu
                                            {...sharedProps}
                                            filters={filters}
                                            uriRef={generatedUriRef}
                                        />

                                        {/* Ad */}
                                        {!isThinPage && isSkyscraperAdEnabled && (
                                            <SbSharedSkyscraperAd searchBrowse={searchBrowse} />
                                        )}
                                    </div>
                                )
                            }
                        </ServerVarsContextConsumer>

                        <div className={styles.resultsContainer}>
                            {/* Content Module */}
                            {isContentModuleEnabled && (
                                <SbRespContentModuleSelector searchBrowse={searchBrowse} />
                            )}
                            <div className={styles.resultsHeader}>
                                <div className={styles.sortContainer}>
                                    <SbRespSortItems searchBrowse={searchBrowse} />
                                    <SbRespPageSize searchBrowse={searchBrowse} />
                                </div>
                                <SbRespBrowseHeader searchBrowse={searchBrowse} />
                            </div>

                            {/* Item Results */}
                            <SbRespSearchResultContainer
                                {...sharedProps}
                                showInternalSellerName={canShowInternalSellerName}
                                showSellerName={!!canShowSellerName}
                                showCreatorName={canShowCreatorName}
                                showMeasurements={showMeasurements}
                                relay={relay}
                            />
                            {/* Related Items */}
                            {isThinBrowsePage && (
                                <SbSharedRelatedItems
                                    searchBrowse={searchBrowse}
                                    user={user}
                                    currency={currency}
                                />
                            )}
                            {/* Pagination */}
                            <div className={styles.paginationContainer}>
                                <SbSharedPagination
                                    currentPage={page}
                                    numberOfPages={displayMaxNumberOfPages}
                                    displayUriRef={displayUriRef}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Follow Search */}
                    <SbRespFollowSearchBanner
                        viewer={viewer}
                        user={user}
                        showFollowButton={showFollowButton}
                        isFollowing={isFollowing}
                        followSearchValue={followSearchValue}
                        uriRef={generatedUriRef}
                        buttonSize="large"
                        refetchFollow={refetchFollow}
                    />
                    {/* Ad */}
                    {!isThinPage && isBillboardAdEnabled && (
                        <SbSharedBillboardAd searchBrowse={searchBrowse} adWidth="desktop" />
                    )}

                    {/* Recently Viewed Items */}
                    {<SharedRecentlyViewedItems isClient={isClient} user={user} />}

                    {/*  More Ways to Browse */}
                    {isMoreWaysToBrowseEnabled && (
                        <SbRespMoreWaysToBrowse searchBrowse={searchBrowse} />
                    )}

                    {/* Overlay Loader */}
                    {refetchInFlight && <LoadingComponent />}

                    {/* Unavailable Item Modal */}
                    <SbRespNotificationUnavailableItem />

                    {/* Personalized Filter Toast */}
                    <SbSharedPersonalizedFilterToast shippingFilters={shippingFilters} />
                </React.Fragment>
            )}
        </SbSharedFollowSearchProvider>
    );
}

SbRespComponentsComponent.propTypes = {
    searchBrowse: PropTypes.shape({
        displayUriRef: PropTypes.string.isRequired,
        topCategoryL1: PropTypes.string,
        filters: PropTypes.array.isRequired,
    }).isRequired,
    page: PropTypes.number,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    viewer: PropTypes.object,
    refetchInFlight: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    generatedUriRef: PropTypes.string,
    relay: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    internalBuyLinks: PropTypes.object,
    relayVariables: PropTypes.shape({
        isTrade: PropTypes.bool,
    }).isRequired,
    shippingFilters: PropTypes.array,
};

const mapStateToProps = state => {
    const { displayPreferences, filters, relayVariables } = state;

    return {
        showMeasurements: displayPreferences.showMeasurements,
        generatedUriRef: filters.generatedUriRef,
        page: filters.page,
        refetchInFlight: filters.refetchInFlight,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespComponents = createFragmentContainer(
    connect(mapStateToProps)(SbRespComponentsComponent),
    {
        searchBrowse: graphql`
            fragment SbRespComponents_searchBrowse on SearchBrowseQueryConnection {
                displayUriRef
                topCategoryL1
                totalResults
                displayMaxNumberOfPages
                canShowNoAttributeSearch
                pageType
                spellCheck {
                    newQuery
                    redirect {
                        redirectFrom
                    }
                }
                ...SbSharedSearchSuggestions_searchBrowse
                ...SbSharedSearchRedirectNotice_searchBrowse
                ...SbRespRefineMenu_searchBrowse
                ...SbRespContentModuleSelector_searchBrowse
                ...SbRespNullSearchResults_searchBrowse
                ...SbSharedPageBreadcrumbs_searchBrowse
                ...SbRespSearchResultContainer_searchBrowse
                ...SbSharedTitle_searchBrowse
                ...SbRespBrowseHeader_searchBrowse
                ...SbRespSortItems_searchBrowse
                ...SbRespPageSize_searchBrowse
                ...SbSharedTopTileController_searchBrowse
                ...SbRespCategoryTiles_searchBrowse
                ...SbSharedFollowSearchProvider_searchBrowse
                ...SbSharedBillboardAd_searchBrowse
                ...SbSharedSkyscraperAd_searchBrowse
                ...SbSharedRelatedItems_searchBrowse
                ...SbRespMoreWaysToBrowse_searchBrowse
                ...SbRespPriceTiles_searchBrowse @include(if: $isTrade)
                ...SbRespTradeOnApprovalPromoBanner_searchBrowse @include(if: $isTrade)
                filters {
                    ...SbRespRefineMenu_filters
                }
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
            }
        `,
        user: graphql`
            fragment SbRespComponents_user on User {
                canShowSellerName
                canShowInternalSellerName: hasPermission(name: "VIEW_SELLER_NAME")
                ...TradePromo_user @include(if: $isTrade)
                ...SbRespRefineMenu_user
                ...SbRespNullSearchResults_user
                ...SbRespFollowSearchBanner_user
                ...SbRespFollowSearchTopButton_user
                ...SbRespSearchResultContainer_user
                ...SbSharedFollowSearchProvider_user
                ...SbSharedRelatedItems_user
                ...SharedRecentlyViewedItems_user
            }
        `,
        viewer: graphql`
            fragment SbRespComponents_viewer on Viewer {
                isSkyscraperAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isContentModuleEnabled: isEnabledByLocale(featureName: "BROWSE_CONTENT_MODULES")
                isBillboardAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isThinSearchBrowseEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isMoreWaysToBrowseEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isFollowSearchEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                ...SbRespSearchResultContainer_viewer
                ...SbRespRefineMenu_viewer
                ...SbRespNullSearchResults_viewer
                ...SbRespFollowSearchBanner_viewer
                ...SbSharedTopTileController_viewer
                ...TradePromo_viewer @include(if: $isTrade)
                ...SbRespPriceTiles_viewer @include(if: $isTrade)
            }
        `,
        shippingFilters: graphql`
            fragment SbRespComponents_shippingFilters on ShippingFilterType @relay(plural: true) {
                ...SbSharedPersonalizedFilterToast_shippingFilters
            }
        `,
    }
);
